import type { ILocTreeItem } from '../types';
import type { PsHttpSSE } from '@patsnap-utils/sse';
import type { GetDownloadUrlRequest, UploadedFileInfo } from '@telecom/shared/domain/file-upload';
import type { PatentMessage } from '../flow/stages/image_search/shared';
import type { ModelStrategy } from '@telecom/shared/domain/user-settings';

/**
 * LOC分类项
 */
export interface LocItem {
    /** LOC分类号 */
    id: string;
    /** LOC解释 */
    desc?: string;
}
type DateOperator = 'gte' | 'lte' | 'gt' | 'lt' | 'eq'
/**
 * 外观FTO搜索条件
 */
export interface DesignFtoSearchConditions {
    /** 任务ID（必填，由前端生成，用于外部服务追踪） */
    task_id: string;
    /** 目标图片path */
    target_image_key: string;
    /** 目标图片URL */
    target_image_url: string;
    /** 受理局代码列表（可选） */
    country?: string[];
    /** LOC分类项列表（可选） */
    loc_items?: LocItem[];
    /** 产品描述，可选 */
    description?: string;
    /** @deprecated 主要字段（关键词，可选） */
    main_field?: string;
    /** 申请起始时间（YYYYMMDD格式，可选） */
    // apply_start_time?: string;
    date_filters?: {
        APD?: { operator: 'lte' | 'gte'; value: string } | { operator: 'between'; start: string; end: string };
        PBD?: { operator: 'lte' | 'gte'; value: string } | { operator: 'between'; start: string; end: string };
    };
    /** 历史记录标题 */
    title: string;
    /** 仅包含企业 */
    include_company?: string[];
    /** 排除企业 */
    exclude_company?: string[];
    /** 模型选择策略 */
    model_strategy?: ModelStrategy;
}

export interface DesignFtoBatchTaskResponse {
    /** 批次ID */
    batch_id: string;
    /** 任务总数 */
    total_count: number;
    /** 预估时间 */
    estimated_duration: number;
}

/**
 * Stage名称枚举
 */
export enum StageName {
    /** 实物图转线条图 */
    ConvertLineart = 'convert_lineart',
    /** 专利图像检索 */
    ImageSearch = 'image_search',
    /** AI智能重排序 */
    Rerank = 'rerank',
    /** 多源融合排序 */
    RrfFusion = 'rrf_fusion',
    /** FTO分析报告生成 */
    GenerateReport = 'generate_report',
}

/**
 * Stage状态枚举
 */
export enum StageStatus {
    /** 待执行 */
    Pending = 'pending',
    /** 执行中 */
    Running = 'running',
    /** 已完成 */
    Completed = 'completed',
    /** 失败 */
    Failed = 'failed',
    /** 跳过 */
    Skipped = 'skipped',
    /** 中断 */
    Interrupted = 'interrupted',
}

/**
 * 工作流状态枚举
 */
export enum WorkflowStatus {
    /** 待执行 */
    Pending = 'pending',
    /** 执行中 */
    Running = 'running',
    /** 已完成 */
    Completed = 'completed',
    /** 失败 */
    Failed = 'failed',
    /** 已取消 */
    Cancelled = 'cancelled',
}

/**
 * Stage基本信息
 */
export interface StageInfo {
    /** Stage名称 */
    stage: StageName;
    /** Stage顺序 */
    stage_order: number;
    /** Stage状态 */
    stage_status: StageStatus;
    /** Stage固定文案描述 */
    stage_description: string;
    /** 预估执行时长（秒） */
    estimated_duration: number;
    /** 实际执行时长（秒） */
    execution_duration?: number;
    /** 重试次数 */
    retry_count: number;
    /** 错误信息 */
    error_message?: string;
    /** 创建时间 */
    created_at?: string;
}

/**
 * Stage详细结果
 */
export interface StageResultDetail {
    /** 执行记录ID */
    id: number;
    /** 任务ID */
    task_id: string;
    /** Stage名称 */
    stage: StageName;
    /** Stage顺序 */
    stage_order: number;
    /** Stage状态 */
    stage_status: StageStatus;
    /** 业务结果数据 */
    result_data?: Record<string, any>;
    /** 执行时长 */
    execution_duration?: number;
    /** 预估时长 */
    estimated_duration?: number;
    /** Stage描述 */
    stage_description?: string;
    /** 重试次数 */
    retry_count: number;
    /** 错误信息 */
    error_message?: string;
    /** 错误堆栈 */
    error_traceback?: string;
    /** 创建时间 */
    created_at?: string;
}

/**
 * 任务详情
 */
export interface TaskDetail {
    /** 任务ID */
    task_id: string;
    /** 用户ID */
    user_id?: string;
    /** 任务状态 */
    workflow_status: WorkflowStatus;
    /** 当前stage */
    current_stage?: StageName;
    /** 当前stage顺序 */
    current_stage_order?: number;
    /** 标题 */
    title: string;
    /** 目标图片Path */
    target_image_key: string;
    /** 目标图片URL */
    target_image_url: string;
    /** 国家代码 */
    country?: string;
    /** LOC分类号列表 */
    loc_items?: string[];
    /** 主要领域 */
    main_field?: string;
    /** 申请起始时间 */
    apply_start_time?: string;
    /** 搜索结果总数 */
    total_search_result_count?: number;
    /** 总执行时长 */
    total_execution_duration?: number;
    /** 重试次数 */
    retry_count: number;
    /** 错误信息 */
    error_message?: string;
    /** 出错stage */
    error_stage?: StageName;
    /** 创建时间 */
    created_at?: string;
    /** 更新时间 */
    updated_at?: string;
}

/**
 * 工作流流程状态响应
 */
export interface WorkflowFlowStatusResponse {
    /** 任务信息 */
    task: TaskDetail;
    /** Stage列表 */
    stages: StageInfo[];
}

/**
 * 流事件类型枚举
 */
export enum FlowEventType {
    /** 任务信息 */
    TASK_INFO = 'task_info',
    /** Stage更新 */
    STAGE_UPDATE = 'stage_update',
    /** Stage处理中 */
    STAGE_PROGRESS = 'stage_progress',
    /** 工作流完成 */
    COMPLETE = 'complete',
    /** 错误 */
    ERROR = 'error',
}

/**
 * SSE事件类型 - 任务信息
 */
export interface TaskInfoEvent extends TaskDetail {
    /** 事件类型 */
    event_type: FlowEventType.TASK_INFO;
    /** 任务ID */
    task_id: string;
    /** 任务状态 */
    workflow_status: WorkflowStatus;
    /** 当前stage */
    current_stage?: StageName;
    /** 当前stage顺序 */
    current_stage_order?: number;
}

/**
 * SSE事件类型 - Stage更新
 */
export interface StageUpdateEvent {
    /** 事件类型 */
    event_type: FlowEventType.STAGE_UPDATE;
    /** Stage名称 */
    stage: StageName;
    /** Stage顺序 */
    stage_order: number;
    /** Stage状态 */
    status: StageStatus;
    /** Stage名字 */
    name: string;
    /** Stage描述 */
    description: string;
    /** Stage的长描述 */
    long_description: string;
    /** 当前状态对应的动态文案 */
    status_text: string;
    /** 预估时长（秒） */
    estimated_duration: number;
    /** 实际时长（秒） */
    actual_duration?: number;
    /** 当前重试次数 */
    retry_count: number;
    /** 最大重试次数 */
    max_retries: number;
    /** 时间戳 */
    timestamp?: string;
}

export interface StageProgressEvent extends Record<string, any> {
    /** 事件类型 */
    event_type: FlowEventType.STAGE_PROGRESS;
    /** Stage名称 */
    stage: StageName;
    /** 消息 */
    message: string;
    /** 任务ID */
    task_id: string;
    /** 时间戳 */
    timestamp?: string;
    /** 进度 */
    progress?: number;
    /** 总数 */
    total?: number;
    /** 当前进度百分比 */
    percentage?: number;
    /** 专利信息 */
    patent_infos?: {
        patent_pn: string;
        patent_id: string;
        img_id: string;
        title: string;
    }[]
}

/**
 * SSE事件类型 - 工作流完成
 */
export interface WorkflowCompleteEvent {
    /** 事件类型 */
    event_type: FlowEventType.COMPLETE;
    /** 任务ID */
    task_id: string;
    /** 最终状态 */
    status: WorkflowStatus;
    /** 总时长（秒） */
    total_duration?: number;
    /** 错误信息（如果失败） */
    error_message?: string;
}

/**
 * SSE事件类型 - 错误
 */
export interface FlowStatusErrorEvent {
    /** 事件类型 */
    event_type: FlowEventType.ERROR;
    /** 错误信息 */
    error: string;
}

/**
 * 流状态 SSE 消息类型
 */
export type FlowStatusSSEMessage =
    | TaskInfoEvent
    | StageUpdateEvent
    | StageProgressEvent
    | WorkflowCompleteEvent
    | FlowStatusErrorEvent;

/**
 * 分享任务响应
 */
export interface ShareTaskResponse {
    /** 任务ID */
    task_id: string;
    /** 是否公开 */
    is_public: boolean;
}

export type ExportReportStatus = 'queued' | 'running' | 'completed' | 'failed';

/**
 * 导出报告响应
 */
export interface ExportReportResponse {
    /** 是否成功提交/查询 */
    success?: boolean;
    /** 提示信息 */
    message?: string;
    /** 导出任务ID */
    export_id?: string;
    /** 任务ID */
    task_id: string;
    /** 导出状态 */
    status?: ExportReportStatus;
    /** 当前进度 */
    progress?: number;
    /** 总进度 */
    total?: number;
    /** 报告下载URL */
    download_url?: string | null;
    /** 状态查询URL */
    status_url?: string;
    /** 错误信息 */
    error_message?: string;
    /** 文件名 */
    file_name?: string;
}

/**
 * 任务存在性检查响应
 */
export interface TaskExistsResponse {
    /** 任务ID */
    task_id: string;
    /** 任务是否存在 */
    exists: boolean;
    /** 任务状态（如果存在） */
    status?: WorkflowStatus;
    /** 当前stage（如果存在） */
    current_stage?: StageName;
    /** 创建时间（如果存在） */
    created_at?: string;
    /** 是否公开（如果存在） */
    is_public?: boolean;
    /** 当前用户是否是任务的创建者（如果存在），用于判断是否有编辑/删除权限 */
    is_owner?: boolean;
}

export interface PatentIdImageId {
    patent_id: string
    image_id: string
}

/**
 * 外观FTO 历史记录任务来源
 */
export type DesignFtoHistorySource = 'regular' | 'mcp';

/**
 * 外观FTO 历史记录任务的查询条件快照（提交任务时的查询条件）
 */
export interface DesignFtoHistoryQueryConditions {
    /** 受理局代码列表 */
    country?: string[] | null;
    /** LOC分类项（字符串或对象兼容） */
    loc_items?: Array<string | LocItem> | null;
    /** 主要字段（已废弃） */
    main_field?: string | null;
    /** 日期过滤条件 */
    date_filters?: Record<string, any> | null;
    /** 仅包含企业 */
    include_company?: string[] | null;
    /** 排除企业 */
    exclude_company?: string[] | null;
    /** 法律状态过滤 */
    simple_legal_status?: string | null;
}

/**
 * 外观FTO 历史记录项
 */
export interface DesignFtoHistoryItem {
    /** 任务ID */
    task_id: string;
    /** 任务来源 */
    source?: DesignFtoHistorySource;
    /** 任务状态 */
    workflow_status?: WorkflowStatus;
    /** 当前执行节点（完成后通常为 null） */
    current_stage?: StageName | null;
    /** 当前执行节点序号 */
    current_stage_order?: number | null;
    /** 目标图片URL */
    target_image_url?: string;
    /** 目标图片S3 key */
    target_image_key?: string;
    /** 任务标题 */
    title: string;
    /** 提交任务时的查询条件 */
    query_conditions?: DesignFtoHistoryQueryConditions;
    /** 检索结果总数 */
    total_search_result_count?: number;
    /** 总耗时（秒） */
    total_execution_duration?: number;
    /** 失败原因 */
    error_message?: string | null;
    /** 失败节点 */
    error_stage?: StageName | null;
    /** 创建时间（ISO 格式） */
    created_at: string;
    /** 更新时间（ISO 格式） */
    updated_at?: string;
}

/**
 * 外观FTO 历史记录列表请求参数
 *
 * 分页规则：
 * - 首次进入历史页时，不传 offset 和 limit，接口默认返回最新 3 条；
 * - 继续加载时，传入上一轮返回的 next_offset，默认每页 15 条；
 * - 可通过 limit 自定义返回数量，最大 50。
 */
export interface DesignFtoHistoryListRequest {
    /** 分页偏移量，不传时返回最新 3 条；传入后默认返回 15 条 */
    offset?: number;
    /** 返回数量，最大 50 */
    limit?: number;
    /** 任务来源过滤 */
    source?: DesignFtoHistorySource;
}

/**
 * 外观FTO 历史记录列表响应
 */
export interface DesignFtoHistoryListResponse {
    /** 历史记录列表 */
    items: DesignFtoHistoryItem[];
    /** 当前用户符合条件的历史任务总数 */
    total: number;
    /** 本次查询偏移量 */
    offset: number;
    /** 本次返回数量限制 */
    limit: number;
    /** 是否还有更多数据 */
    has_more: boolean;
    /** 下一页偏移量；没有更多数据时为 null */
    next_offset: number | null;
}

export interface PatentBatchImagesResponse {
    [patentId: string]: {
        [imageId: string]: string
    }
}

/** 专利图像信息 */
export interface PatentImage {
    labels?: string[];
    extracted: boolean;
    patent_id: string;
    fig_title_code?: string;
    fulltext_image240_url: string;
    official_size?: string;
    image_id: string;
    image_from?: string;
    storage_path: string;
    source_image_type?: string;
    image_type?: string;
    is_extracted: boolean;
}

/**
 * 外观FTO API服务接口
 */
export interface DesignFtoApi {
    /**
     * 提交批量任务
     * @param images - 图片列表（1-100个）
     */
    submitBatchTask(images: DesignFtoSearchConditions[]): Promise<DesignFtoBatchTaskResponse>;

    /**
     * 上传文件，file 和 url 必填一个
     * @param file - 文件对象 或 文件 URL
     */
    uploadFile(file: File | string): Promise<UploadedFileInfo>;

    /**
     * 获取文件签名地址
     * @param request 请求参数
     * @returns 签名后的URL
     */
    signFile(request: GetDownloadUrlRequest): Promise<string>;

    /**
     * 获取LOC分类树节点数据
     * @param code - LOC分类代码，为空则获取根节点
     */
    getLOCItemsData(code?: string): Promise<ILocTreeItem[]>;

    /**
     * 根据关键词搜索LOC分类树
     * @param keyword - 搜索关键词
     */
    getLOCTreeByKeyword(keyword: string): Promise<{
        query_results: Array<{
            cls_tree: ILocTreeItem;
        }>;
    }>;

    /**
     * 根据图片预测LOC分类
     * @param params - 预测参数
     */
    predictLOC(params: { url?: string; description?: string }): Promise<ILocTreeItem[]>;

    /**
     * 获取工作流流程状态（SSE流式接口）
     * @param taskId - 任务ID
     * @returns PsHttpSSE实例，用于监听事件和控制流
     */
    getFlowStatus(taskId: string): Promise<PsHttpSSE<FlowStatusSSEMessage>>;

    /**
     * 获取Stage执行结果
     * @param taskId - 任务ID
     * @param stage - Stage名称
     * @param version - 版本，默认为 latest（最新），也可以指定 execution_id
     */
    getStageResult(taskId: string, stage: StageName, version?: string): Promise<StageResultDetail>;

    /**
     * 获取工作流流程状态快照（非流式，一次性返回所有数据）
     * @param taskId - 任务ID
     */
    getFlowStatusSnapshot(taskId: string): Promise<WorkflowFlowStatusResponse>;

    /**
     * 分享任务（设置为公开状态）
     * @param taskId - 任务ID
     * @description 分享后任何人都可以查看任务状态和结果，只有任务创建者可以执行此操作
     */
    shareTask(taskId: string): Promise<ShareTaskResponse>;

    /**
     * 检查任务是否存在
     * @param taskId - 任务ID
     * @description 用于在调用其他接口前验证任务ID的有效性
     */
    checkTaskExists(taskId: string): Promise<TaskExistsResponse>;

    /**
     * 导出报告
     * @param taskId - 任务ID
     */
    exportReport(taskId: string): Promise<ExportReportResponse>;

    /**
     * 查询报告导出状态
     * @param statusUrl - 导出状态查询URL
     */
    getExportReportStatus(statusUrl: string): Promise<ExportReportResponse>;

    /**
     * 编辑并重试指定 Stage
     * @param taskId - 任务ID
     * @param stage - 要重试的 Stage 名称
     * @param editedState - 要修改的状态字段（部分更新）
     */
    editAndRetry(taskId: string, stage: string, editedState: Record<string, any>): Promise<{ success: boolean; message: string }>;

    /**
     * 根据文本模糊查询专利信息
     * @param q - 专利公开号或关键词
     */
    patentSearch(q: string): Promise<(PatentMessage & { pdf_image: PatentImage[] }) | null>;

    // 新接口：传 patent_id 和 image_id
    patentBatchImagesByImageId: (patent_id_image_ids: PatentIdImageId[]) => Promise<PatentBatchImagesResponse>

    /**
     * 获取外观FTO 历史任务记录列表
     * @param params - 分页参数
     */
    getHistoryList(params?: DesignFtoHistoryListRequest): Promise<DesignFtoHistoryListResponse>;
}

export const DesignFtoApi = Symbol.for('DesignFtoApi');
